import { Tabs as BaseTabs } from '@base-ui-components/react/tabs';
import { createContext, useContext } from 'react';
import { cn } from '../cn.js';

/**
 * Tabs: one surface, several views of it.
 *
 * Base UI owns roving focus, the arrow keys and the ARIA relationship between a tab and its
 * panel. This owns what they look like, and offers three shapes because the one shape it had was
 * right for a page and wrong everywhere else.
 *
 * | variant | for | reads as |
 * |---|---|---|
 * | `underline` | sections of a page — the default | a heading row |
 * | `segmented` | a view switch inside a panel: chat or table, day or week | one control |
 * | `pill` | filtering a list by a set of states | a row of chips |
 *
 * Choosing between them is a question about what the tabs *are*, not about taste. A segmented
 * control says the choices are alternatives of equal weight and that the switch is cheap; an
 * underline says the page has sections; pills say the list below is being narrowed.
 */
export type TabsVariant = 'underline' | 'segmented' | 'pill';
export type TabsSize = 'sm' | 'md';

interface TabsListContext {
  readonly variant: TabsVariant;
  readonly size: TabsSize;
}

/**
 * The list tells its tabs what shape they are in.
 *
 * Through context rather than a prop on every tab: a list whose tabs disagree about their variant
 * is not a thing anybody wants, and making it unrepresentable is cheaper than documenting it.
 */
const TabsListContext = createContext<TabsListContext>({ variant: 'underline', size: 'md' });

export const Tabs = BaseTabs.Root;

export interface TabsListProps extends BaseTabs.List.Props {
  readonly variant?: TabsVariant;
  readonly size?: TabsSize;
  /** Spreads the tabs across the full width, each taking an equal share. */
  readonly justified?: boolean;
}

const listByVariant: Record<TabsVariant, string> = {
  underline: 'relative gap-(--space-xs) border-b border-(--color-border)',
  segmented:
    'relative gap-(--space-xs) rounded-(--radius-md) border border-(--color-border) bg-(--color-surface-sunken) p-(--space-xs)',
  pill: 'gap-(--space-xs)',
};

export function TabsList({
  className,
  variant = 'underline',
  size = 'md',
  justified = false,
  children,
  ...props
}: TabsListProps) {
  return (
    <TabsListContext.Provider value={{ variant, size }}>
      <BaseTabs.List
        className={cn(
          'flex items-center',
          /*
           * **A tab strip scrolls rather than taking the page with it**, and `min-w-0` is the
           * half that makes `overflow-x-auto` mean anything: a flex item's automatic minimum size
           * is its min-content width, so without it the list refuses to shrink and the *document*
           * grows instead. `Table` learned the same thing on the queue board in M4.5-11, and this
           * is the same bug in a different component — four tabs do not fit 320 pixels, and
           * WCAG 1.4.10 is about the page rather than about what is inside a component.
           */
          'min-w-0 overflow-x-auto',
          /*
           * **And it never scrolls vertically.** `overflow-x: auto` makes `overflow-y` compute to
           * `auto` as well, and the underline sat one pixel below the list, so every underline
           * strip in the product drew a one-pixel vertical scrollbar at its end (M5.5-01). The
           * indicator now sits inside the list, on its border, and nothing overflows downwards.
           */
          'overflow-y-hidden',
          listByVariant[variant],
          justified && '[&>*]:flex-1',
          className,
        )}
        {...props}
      >
        {/*
         * **The indicator comes first, and that is the whole of why the label is readable.**
         *
         * It and the tabs are both positioned at the same level, so whichever is later in the
         * document paints on top. With the indicator last, the segmented variant's pill covered
         * its own label completely: the tab was there, selected, and invisible in both themes. No
         * `z-index` is needed once the order is right — and the `z-(--elevation-base)` that was
         * meant to handle it resolves to `0`, which did nothing at all.
         *
         * The indicator slides; the tabs only change colour. Base UI measures the active tab and
         * publishes `--active-tab-left` and `--active-tab-width`, so one element moves between
         * positions instead of every tab animating a border of its own. Moving one small box is
         * also the only version of this that does not animate text.
         *
         * `pill` has none: with no track behind them there is nothing for an indicator to travel
         * along, and the filled tab is the indicator.
         */}
        {variant === 'pill' ? null : <TabsIndicator variant={variant} />}
        {children}
      </BaseTabs.List>
    </TabsListContext.Provider>
  );
}

function TabsIndicator({ variant }: { readonly variant: Exclude<TabsVariant, 'pill'> }) {
  return (
    <BaseTabs.Indicator
      className={cn(
        'absolute left-0 translate-x-(--active-tab-left) w-(--active-tab-width)',
        // biome-ignore lint: `translate` and `width` are property names, not values a theme could set. They have to be named: Tailwind 4's `translate-x-*` sets the `translate` property rather than `transform`, which the default transition list does not cover — so the indicator would jump rather than slide.
        'transition-[translate,width] duration-(--motion-duration-normal) ease-(--motion-easing-emphasized)',
        'motion-reduce:transition-none',
        variant === 'underline'
          ? 'bottom-0 h-0.5 rounded-(--radius-full) bg-(--color-accent)'
          : 'top-(--space-xs) bottom-(--space-xs) rounded-(--radius-sm) bg-(--color-surface) shadow-(--shadow-raised)',
      )}
    />
  );
}

const tabBySize: Record<TabsSize, string> = {
  sm: 'px-(--space-sm) py-(--space-xs) text-(length:--font-size-caption)',
  md: 'px-(--space-md) py-(--space-sm) text-(length:--font-size-body)',
};

const tabByVariant: Record<TabsVariant, string> = {
  underline: 'data-[selected]:text-(--color-text-primary)',
  // No z-index: the indicator is rendered before the tabs, so the tabs paint over it.
  segmented: 'rounded-(--radius-sm) data-[selected]:text-(--color-text-primary)',
  /*
   * A selected pill is **filled**, not tinted.
   *
   * It was an accent-subtle background with an accent border, which is the treatment a *hover*
   * usually gets — and in a row of six chips the selected one has to be findable at a glance.
   * Filled also survives a tenant replacing the accent, where a faint tint of an unknown hue
   * may not.
   */
  pill: cn(
    'rounded-(--radius-full) border border-(--color-border)',
    'hover:bg-(--color-surface-sunken)',
    'data-[selected]:border-(--color-accent) data-[selected]:bg-(--color-accent)',
    'data-[selected]:text-(--color-accent-foreground)',
    'data-[selected]:hover:bg-(--color-accent-hover)',
  ),
};

export function TabsTab({ className, ...props }: BaseTabs.Tab.Props) {
  const { variant, size } = useContext(TabsListContext);
  return (
    <BaseTabs.Tab
      className={cn(
        'relative inline-flex cursor-pointer items-center justify-center gap-(--space-xs)',
        'font-medium whitespace-nowrap text-(--color-text-secondary)',
        'transition-colors duration-(--motion-duration-fast) ease-(--motion-easing-standard)',
        'hover:text-(--color-text-primary)',
        'outline-none focus-visible:ring-2 focus-visible:ring-(--color-focus-ring) focus-visible:ring-offset-2 focus-visible:ring-offset-(--color-surface)',
        'data-disabled:cursor-not-allowed data-disabled:opacity-(--opacity-disabled)',
        'motion-reduce:transition-none',
        tabBySize[size],
        tabByVariant[variant],
        className,
      )}
      {...props}
    />
  );
}

/**
 * A count or a status beside a tab's label.
 *
 * Its own component because a number in a tab is read as part of the tab's name — "Queued 12" —
 * and a `Badge` dropped in there brings a border and a background that fight the tab's own
 * selected state.
 */
export function TabsCount({ className, ...props }: React.HTMLAttributes<HTMLSpanElement>) {
  return (
    <span
      className={cn(
        'rounded-(--radius-full) bg-(--color-surface-sunken) px-(--space-xs)',
        'text-(length:--font-size-caption) tabular-nums text-(--color-text-secondary)',
        'group-data-[selected]:bg-(--color-accent-subtle) group-data-[selected]:text-(--color-accent)',
        // Inside a filled pill the count sits on the accent itself, so it inverts with it.
        'group-data-[selected]:group-[.rounded-full]:bg-(--color-accent-hover)',
        'group-data-[selected]:group-[.rounded-full]:text-(--color-accent-foreground)',
        className,
      )}
      {...props}
    />
  );
}

export function TabsPanel({ className, ...props }: BaseTabs.Panel.Props) {
  return (
    <BaseTabs.Panel
      className={cn(
        'pt-(--space-md) text-(length:--font-size-body) text-(--color-text-primary)',
        'outline-none focus-visible:ring-2 focus-visible:ring-(--color-focus-ring)',
        className,
      )}
      {...props}
    />
  );
}
